'use client';
import 'react-phone-number-input/style.css'
import { useEffect, useState } from 'react';
import { FormValues, IAccountProps, EditableFieldData } from '@/types';
import { useUser, useSession } from '@clerk/nextjs';
import { toast } from 'sonner';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { CrudAvatarUpload } from './CrudAvatarUpload';
import { FormattedMessage } from "react-intl";
import { useIntl } from 'react-intl';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import { KeenIcon } from '../../common/keenicons';
import { postUserDatat } from '@/lib/action';
import { ScreenLoader } from '../../common/loaders/ScreenLoader';
import { useDispatch } from 'react-redux';

const getInitialFormData = (user: any): FormValues => ({
  firstName: user?.firstName || '',
  lastName: user?.lastName || '',
  email: user?.emailAddresses?.[0]?.emailAddress || '',
  phone: user?.phoneNumbers?.[0]?.phoneNumber || '',
  company: user?.publicMetadata?.company || '',
  city: user?.publicMetadata?.city || '',
  country: user?.publicMetadata?.country || '',
  image: user?.imageUrl || '',
});

const EditableField = ({
  label, field, value, isEditing, isEditable = true, isLoading, onEdit, onChange, onSave, error, touched, dirty, onBlur, isAnyFieldLoading = false,
}: EditableFieldData) => {
  const intl = useIntl();
  const placeholderId = `PLACEHOLDER.${label.split('.').pop()}`;
  return (
    <tr>
      <td className="text-gray-600 font-normal"><FormattedMessage id={label} /></td>
      <td className="text-gray-800 font-normal w-[284.73px]">
        {isEditing ? (
          <>
            {field === 'phone' ? (
              <PhoneInput
                international
                value={value}
                max="12"
                onChange={(val) => onChange(field, val || '')}
                onBlur={onBlur}
                placeholder={intl.formatMessage({ id: placeholderId })}
                className={`input input-sm mr-[-31px] ${error && touched && dirty ? 'border-red-500 focus:ring-red-500' : ''}`}
              />
            ) : (
              <input
                name={field}
                type="text"
                value={value}
                placeholder={intl.formatMessage({ id: placeholderId })}
                className={`input input-sm w-full ${error && touched && dirty ? 'border-red-500 focus:ring-red-500' : ''}`}
                onChange={(e) => onChange(field, e.target.value)}
                onBlur={onBlur}
              />
            )}
            {(touched || dirty) && error && (
              <p className="text-red-500 text-xs mt-1">{error}</p>
            )}
          </>
        ) : (
          <p className={`${!isEditable ? 'text-gray-500' : 'border-1 border-red-700 break-all'}`}>
            {value || <FormattedMessage id="PROFILE.NOT_SET" />}
          </p>
        )}
      </td>
      <td className="text-center">
        {isEditable && (
          <button
            type="button"
            onClick={isEditing ? onSave : onEdit}
            className="btn btn-sm btn-icon btn-clear btn-primary"
            disabled={isLoading || isAnyFieldLoading}
          >
            {isLoading ? (
              <KeenIcon icon="loading" className="animate-spin" />
            ) : isEditing ? (
              <KeenIcon icon="save-2" />
            ) : (
              <KeenIcon icon="notepad-edit" />
            )}
          </button>
        )}
      </td>
    </tr>
  );
};

const Account = ({ title }: IAccountProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { user, isLoaded: userLoaded } = useUser();
  const { session, isLoaded: sessionLoaded } = useSession();
  const [editRow, setEditRow] = useState<string | null>(null);
  const [initialValues, setInitialValues] = useState(getInitialFormData(null));
  const [imageData, setImageData] = useState<string | null>(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [loadingField, setLoadingField] = useState<string | null>(null);
  const [dirtyFields, setDirtyFields] = useState<Record<string, boolean>>({});

  if (!userLoaded || !sessionLoaded) return <ScreenLoader />;
  if (!user || !session) return null;

  const validationSchema = Yup.object({
    firstName: Yup.string()
      .required(intl.formatMessage({ id: 'VALIDATION.FIRST_NAME_REQUIRED' }))
      .min(2, intl.formatMessage({ id: 'VALIDATION.FIRST_NAME_MIN' })),
    lastName: Yup.string()
      .required(intl.formatMessage({ id: 'VALIDATION.LAST_NAME_REQUIRED' }))
      .min(2, intl.formatMessage({ id: 'VALIDATION.LAST_NAME_MIN' })),
    email: Yup.string()
      .required(intl.formatMessage({ id: 'VALIDATION.EMAIL_REQUIRED' }))
      .email(intl.formatMessage({ id: 'VALIDATION.EMAIL_INVALID' }))
      .min(3, intl.formatMessage({ id: 'VALIDATION.EMAIL_MIN_LENGTH' }))
      .max(50, intl.formatMessage({ id: 'VALIDATION.EMAIL_MAX_LENGTH' })),
    phone: Yup.string()
      .required(intl.formatMessage({ id: 'VALIDATION.PHONE_REQUIRED' }))
      .test('is-valid-phone', intl.formatMessage({ id: 'VALIDATION.PHONE_INVALID_NUMBER' }), (value) =>
        isValidPhoneNumber(value || '')
      ),
    company: Yup.string()
      .required(intl.formatMessage({ id: 'VALIDATION.COMPANY_REQUIRED' }))
      .min(2, intl.formatMessage({ id: 'VALIDATION.COMPANY_MIN' })),
    city: Yup.string()
      .required(intl.formatMessage({ id: 'VALIDATION.CITY_REQUIRED' }))
      .min(2, intl.formatMessage({ id: 'VALIDATION.CITY_MIN' })),
    country: Yup.string()
      .required(intl.formatMessage({ id: 'VALIDATION.COUNTRY_REQUIRED' }))
      .min(2, intl.formatMessage({ id: 'VALIDATION.COUNTRY_MIN' })),
    image: Yup.mixed().nullable(),
  });

  useEffect(() => {
    if (removeImage) {
      setImageData(null);
    }
  }, [removeImage]);

  useEffect(() => {
    if (!userLoaded) return;
    if (user) {
      setInitialValues(getInitialFormData(user));
    }
  }, [userLoaded, user]);

  const handleInputChange = (field: string, value: string) => {
    setInitialValues((prev) => ({ ...prev, [field]: value }));
    setDirtyFields((prev) => ({ ...prev, [field]: true }));
    formik.setFieldValue(field, value, true);
  };

  const formik = useFormik<FormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      if (!user?.id) {
        toast.error(intl.formatMessage({ id: 'USER_ID_MISSING' }));
      }
      if (!session?.id) {
        toast.error(intl.formatMessage({ id: 'SESSION_ID_MISSING' }));
      }

      const updatedData: any = {
        first_name: values.firstName || " ",
        last_name: values.lastName || " ",
        phone_number: values.phone || " ",
        city: values.city || " ",
        country: values.country || " ",
        company: values.company || " ",
      };
      if (imageData) {
        updatedData.image = imageData;
      } else if (removeImage) {
        updatedData.remove_image = 1;
      }

      try {
        const res = await postUserDatat(updatedData, user?.id, session?.id);
        if (res?.data?.error || res?.error) {
          toast.error(res?.data?.error || res?.error);
        } else if (res?.data?.user) {
          toast.success(intl.formatMessage({ id: 'DATA_ADDED_SUCCESSFULLY' }));
          formik.setFieldValue('removeImage', false);
        } else {
          toast.error(intl.formatMessage({ id: 'UNEXPECTED_RESPONSE' }));
        }
      } finally {
        await user.reload();
      }
    }
  });

  const fields: [label: string, field: string, editable: boolean][] = [
    ['ACCOUNT.FIRSTNAME', 'firstName', true],
    ['ACCOUNT.LASTNAME', 'lastName', true],
    ['ACCOUNT.EMAIL', 'email', false],
    ['ACCOUNT.PHONENUMBER', 'phone', true],
    ['ACCOUNT.COMPANYNAME', 'company', true],
    ['ACCOUNT.CITY', 'city', true],
    ['ACCOUNT.COUNTRY', 'country', true],
  ];

  const handleSave = async (field: string) => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      setEditRow(firstErrorField);
      Object.values(errors).forEach((errMsg) => {
        if (typeof errMsg === 'string') {
          toast.error(errMsg || intl.formatMessage({ id: 'UNEXPECTED_ERROR' }));
        }
      });
      return;
    }
    setLoadingField(field);
    setEditRow(null);
    await formik.submitForm();
    setLoadingField(null);
  };


  useEffect(() => {
    if (imageData) {
      formik.submitForm();
    }
  }, [imageData]);


  return (
    <div className="card min-w-full">
      <div className="card-header">
        <h3 className="card-title">{title}</h3>
      </div>

      <div className="card-table scrollable-x-auto pb-3">
        <form onSubmit={formik.handleSubmit}>
          <table className="table align-middle text-sm text-gray-500">
            <tbody>
              <tr>
                <td className="w-[10rem] text-gray-600 font-normal"> <FormattedMessage id="ACCOUNT.IMAGE" /></td>
                <td className="text-gray-700 text-2sm font-normal min-w-32">
                  <FormattedMessage id="ACCOUNT.IMAGESIZE" />
                </td>
                <td className="text-center">

                  <CrudAvatarUpload
                    onUploadComplete={(data) => {
                      setImageData(data);
                      formik.setFieldValue('removeImage', false);
                      setRemoveImage(false);
                    }}
                    onremove={() => {
                      formik.setFieldValue('removeImage', true);
                      setRemoveImage(true);
                      formik.submitForm();
                    }}
                  />
                </td>
              </tr>
              {fields.map(([label, field, editable]) => (

                <EditableField
                  key={field}
                  label={label}
                  field={field}
                  value={formik.values[field as keyof FormValues] ?? ''}
                  onChange={handleInputChange}
                  isEditing={editRow === field}
                  isLoading={loadingField === field}
                  onEdit={() => {
                    setEditRow(field);
                    formik.setFieldTouched(field, true);
                  }}
                  isEditable={editable}
                  onSave={() => handleSave(field)}
                  error={formik.errors[field as keyof typeof formik.errors]}
                  touched={formik.touched[field as keyof typeof formik.touched]}
                  dirty={dirtyFields[field]}
                  onBlur={() => formik.setFieldTouched(field, true)}
                  isAnyFieldLoading={loadingField !== null}
                />
              ))}
            </tbody>
          </table>
        </form>
      </div>
    </div>
  );
};

export { Account, type IAccountProps };
